/**
 * TeamRaciView — RACI Matrix visualization with inline editing
 *
 * Displays roles across the top (columns) and Gantt tasks down the left (rows),
 * grouped by phase. Each cell contains the RACI responsibility designation (R/A/C/I).
 *
 * When canEdit is true, clicking a cell opens a compact popover to set or change
 * the RACI type, or remove the assignment entirely.  Mutations use the same API
 * endpoints as the Gantt chart's RoleBadge popover.
 *
 * Data sources:
 * - Gantt tasks: fetchGanttTasksByProject
 * - Task role assignments: fetchTaskRoleAssignments
 * - Role slots: fetchProjectRoleSlots
 *
 * Epic: DEV-625
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { fetchGanttTasksByProject, apiResponseToGanttTask } from "../../api/ganttTasks";
import {
  fetchTaskRoleAssignments,
  type TaskRoleAssignmentEnriched,
  type ResponsibilityType,
  RESPONSIBILITY_TYPES,
  createTaskRoleAssignment,
  updateTaskRoleAssignmentType,
  deleteTaskRoleAssignment,
} from "../../api/ganttTaskRoleAssignments";
import { fetchProjectRoleSlots } from "../../api/projectRoles";
import { getCurrentUserGuid } from "../../api/client";
import { openRaciPrintWindow } from "../../Data/TeamPrint/generateRaciHtml";
import styles from "./TeamRaciView.module.css";

interface TeamRaciViewProps {
  projectGuid: string;
  projectName: string;
  canEdit?: boolean;
}

// ── RACI responsibility mapping to abbreviation ──
const raciMap: Record<string, string> = {
  responsible: "R",
  accountable: "A",
  consulted: "C",
  informed: "I",
};

const raciClassMap: Record<string, string> = {
  responsible: styles.raciR,
  accountable: styles.raciA,
  consulted: styles.raciC,
  informed: styles.raciI,
};

interface ProcessedTask {
  id: string;
  taskName: string;
  isSummary: boolean;
  indentLevel: number;
  percentComplete: number;
}

interface ProcessedRole {
  roleSlotGuid: string;
  roleLabel: string;
}

interface RaciCell {
  assignmentId: string;
  responsibilityType: string;
}

/* ── Popover coordinate helper ── */
interface PopoverTarget {
  taskId: string;
  roleSlotGuid: string;
  rect: DOMRect;
}

const TeamRaciView: React.FC<TeamRaciViewProps> = ({ projectGuid, projectName, canEdit = false }) => {
  const { t } = useAppTranslation("projectTeam");
  const [tasks, setTasks] = useState<ProcessedTask[]>([]);
  const [roles, setRoles] = useState<ProcessedRole[]>([]);
  const [assignments, setAssignments] = useState<TaskRoleAssignmentEnriched[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());
  /** Role slot GUIDs (uppercased) where the current user has an active assignment. */
  const [currentUserRoleGuids, setCurrentUserRoleGuids] = useState<Set<string>>(new Set());
  /** Currently open cell popover (null when closed). */
  const [popoverTarget, setPopoverTarget] = useState<PopoverTarget | null>(null);
  /** True while an API mutation is in-flight. */
  const [mutating, setMutating] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const togglePhase = useCallback((taskId: string) => {
    setCollapsedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  // ── Load data ──
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch Gantt tasks
        const taskResponses = await fetchGanttTasksByProject(projectGuid);
        const processedTasks: ProcessedTask[] = taskResponses.map((r, i) => {
          const task = apiResponseToGanttTask(r, i);
          return {
            id: task.id,
            taskName: task.taskName,
            isSummary: task.isSummary,
            indentLevel: task.indentLevel,
            percentComplete: task.percentComplete,
          };
        });
        setTasks(processedTasks);

        // Fetch role assignments
        const assignmentData = await fetchTaskRoleAssignments(projectGuid);
        setAssignments(assignmentData);

        // Fetch role slots and build unique roles list
        const roleSlots = await fetchProjectRoleSlots(projectGuid);
        // Only include roles that have at least one RACI assignment
        const rolesWithAssignments = new Set(
          assignmentData.map((a) => a.roleSlotGuid.toUpperCase())
        );
        const processedRoles: ProcessedRole[] = roleSlots
          .filter((slot) => rolesWithAssignments.has(slot.roleSlotGuid.toUpperCase()))
          .map((slot) => ({
            roleSlotGuid: slot.roleSlotGuid,
            roleLabel: slot.roleLabel,
          }))
          .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));

        setRoles(processedRoles);

        // Resolve which role slots belong to the current user
        const meGuid = (getCurrentUserGuid() ?? "").toUpperCase();
        if (meGuid) {
          const myRoles = new Set<string>();
          for (const slot of roleSlots) {
            const hasActiveAssignment = (slot.assignments ?? []).some(
              (a) =>
                a.userGuid.toUpperCase() === meGuid &&
                (a.status === "active" || a.status === "pending_verification")
            );
            if (hasActiveAssignment) {
              myRoles.add(slot.roleSlotGuid.toUpperCase());
            }
          }
          setCurrentUserRoleGuids(myRoles);
        }
      } catch (err) {
        console.error("[TeamRaciView] Failed to load data:", err);
        setError(t("raci.loadError") || "Failed to load RACI data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // DEV-1007: Intentional — t (i18next translator) regenerates on namespace
    // load events and re-firing this effect causes an infinite re-fetch loop
    // that visually presents as the RACI matrix "flashing". Same class as
    // DEV-972 (WorkspaceList) and the post-DEV-932 react-hook rules in
    // CLAUDE.md. Stale t() in the catch is acceptable — the namespace it
    // reads ("projectTeam") doesn't hot-swap and we have a fallback string.
  }, [projectGuid]);

  // ── Build lookup map for O(1) cell access ──
  // Now includes the assignment ID so we can mutate specific records.
  const assignmentMap = useMemo(() => {
    const map = new Map<string, Map<string, RaciCell>>();
    for (const assignment of assignments) {
      const taskKey = assignment.ganttTaskId.toUpperCase();
      const roleKey = assignment.roleSlotGuid.toUpperCase();

      if (!map.has(taskKey)) {
        map.set(taskKey, new Map());
      }
      map.get(taskKey)!.set(roleKey, {
        assignmentId: assignment.id,
        responsibilityType: assignment.responsibilityType,
      });
    }
    return map;
  }, [assignments]);

  // ── Compute visible tasks (respecting collapsed phases) ──
  // NOTE: This hook MUST be before any early returns to satisfy Rules of Hooks.
  const visibleTasks = useMemo(() => {
    const result: ProcessedTask[] = [];
    let skipUntilIndent: number | null = null;

    for (const task of tasks) {
      if (skipUntilIndent !== null) {
        if (task.indentLevel > skipUntilIndent) continue;
        skipUntilIndent = null;
      }

      result.push(task);

      if (task.isSummary && collapsedPhases.has(task.id)) {
        skipUntilIndent = task.indentLevel;
      }
    }
    return result;
  }, [tasks, collapsedPhases]);

  // ── Close popover on outside click ──
  useEffect(() => {
    if (!popoverTarget) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverTarget(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [popoverTarget]);

  // ── Cell click handler ──
  const handleCellClick = useCallback(
    (taskId: string, roleSlotGuid: string, e: React.MouseEvent<HTMLTableCellElement>) => {
      if (!canEdit) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverTarget((prev) => {
        // Toggle off if clicking the same cell
        if (prev && prev.taskId === taskId && prev.roleSlotGuid === roleSlotGuid) return null;
        return { taskId, roleSlotGuid, rect };
      });
    },
    [canEdit]
  );

  // ── API mutations with optimistic local updates ──

  const handleSetType = useCallback(
    async (newType: ResponsibilityType) => {
      if (!popoverTarget || mutating) return;
      const { taskId, roleSlotGuid } = popoverTarget;
      const taskKey = taskId.toUpperCase();
      const roleKey = roleSlotGuid.toUpperCase();
      const existing = assignmentMap.get(taskKey)?.get(roleKey);

      setPopoverTarget(null);
      setMutating(true);

      try {
        if (existing) {
          // Update existing assignment
          if (existing.responsibilityType === newType) return; // no change
          await updateTaskRoleAssignmentType(projectGuid, existing.assignmentId, newType);
          // Optimistic update
          setAssignments((prev) =>
            prev.map((a) =>
              a.id === existing.assignmentId
                ? { ...a, responsibilityType: newType }
                : a
            )
          );
        } else {
          // Create new assignment
          const result = await createTaskRoleAssignment(projectGuid, {
            ganttTaskId: taskId,
            roleSlotGuid,
            responsibilityType: newType,
          });
          // Refresh assignments to pick up the enriched response
          const refreshed = await fetchTaskRoleAssignments(projectGuid);
          setAssignments(refreshed);
        }
      } catch (err) {
        console.error("[TeamRaciView] Mutation failed:", err);
        // Refresh to restore consistent state
        const refreshed = await fetchTaskRoleAssignments(projectGuid);
        setAssignments(refreshed);
      } finally {
        setMutating(false);
      }
    },
    [popoverTarget, mutating, assignmentMap, projectGuid]
  );

  const handleRemove = useCallback(async () => {
    if (!popoverTarget || mutating) return;
    const { taskId, roleSlotGuid } = popoverTarget;
    const taskKey = taskId.toUpperCase();
    const roleKey = roleSlotGuid.toUpperCase();
    const existing = assignmentMap.get(taskKey)?.get(roleKey);

    if (!existing) {
      setPopoverTarget(null);
      return;
    }

    setPopoverTarget(null);
    setMutating(true);

    try {
      await deleteTaskRoleAssignment(projectGuid, existing.assignmentId);
      // Optimistic remove
      setAssignments((prev) => prev.filter((a) => a.id !== existing.assignmentId));
    } catch (err) {
      console.error("[TeamRaciView] Delete failed:", err);
      const refreshed = await fetchTaskRoleAssignments(projectGuid);
      setAssignments(refreshed);
    } finally {
      setMutating(false);
    }
  }, [popoverTarget, mutating, assignmentMap, projectGuid]);

  // ── Print handler ──
  const handlePrint = useCallback(() => {
    openRaciPrintWindow(tasks, roles, assignments, projectName, currentUserRoleGuids);
  }, [tasks, roles, assignments, projectName, currentUserRoleGuids]);

  // ── Early returns for loading / error / empty states ──
  if (loading) {
    return (
      <div className={styles.raciContainer}>
        <div className={styles.emptyState}>{t("raci.loading")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.raciContainer}>
        <div className={styles.emptyState}>{error}</div>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className={styles.raciContainer}>
        <div className={styles.emptyState}>{t("raci.noAssignments")}</div>
      </div>
    );
  }

  // ── Resolve popover cell data ──
  const popoverCell = popoverTarget
    ? assignmentMap
        .get(popoverTarget.taskId.toUpperCase())
        ?.get(popoverTarget.roleSlotGuid.toUpperCase()) ?? null
    : null;

  // ── Render RACI matrix ──
  return (
    <div className={styles.raciContainer}>
      <div className={styles.raciToolbar}>
        <button
          type="button"
          className={styles.printBtn}
          onClick={handlePrint}
          title={t("raci.printBtn") || "Print"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {t("raci.printBtn") || "Print"}
        </button>
      </div>
      <table className={styles.raciTable}>
        <thead>
          <tr>
            <th className={styles.taskHeaderCell}>{t("raci.taskColumn")}</th>
            {roles.map((role) => {
              const isMine = currentUserRoleGuids.has(role.roleSlotGuid.toUpperCase());
              return (
                <th
                  key={role.roleSlotGuid}
                  className={`${styles.raciHeaderCell} ${isMine ? styles.myRoleHeader : ""}`}
                >
                  {isMine && <span className={styles.youBadge}>{t("raci.you") || "You"}</span>}
                  <div className={styles.headerLabel}>{role.roleLabel}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visibleTasks.map((task) => (
            <tr
              key={task.id}
              className={
                task.isSummary ? styles.phaseRow : styles.dataRow
              }
            >
              {/* Task name cell */}
              <td className={styles.taskNameCell}>
                <div
                  className={styles.taskNameText}
                  style={{
                    paddingInlineStart: `${task.indentLevel * 16}px`,
                  }}
                >
                  {task.isSummary ? (
                    <span
                      className={styles.collapseToggle}
                      onClick={() => togglePhase(task.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          togglePhase(task.id);
                        }
                      }}
                    >
                      {collapsedPhases.has(task.id) ? '▸' : '▾'}
                    </span>
                  ) : (
                    <span className={styles.collapseToggleSpacer} />
                  )}
                  {!task.isSummary && task.percentComplete >= 100 && (
                    <span className={styles.statusDot} title={t("raci.complete") || "Complete"} />
                  )}
                  {task.taskName}
                </div>
              </td>

              {/* RACI cells */}
              {roles.map((role) => {
                const taskKey = task.id.toUpperCase();
                const roleKey = role.roleSlotGuid.toUpperCase();
                const cell = assignmentMap.get(taskKey)?.get(roleKey);
                const isMine = currentUserRoleGuids.has(roleKey);
                const isEditable = canEdit && !task.isSummary;
                const isActive =
                  popoverTarget?.taskId === task.id &&
                  popoverTarget?.roleSlotGuid === role.roleSlotGuid;

                return (
                  <td
                    key={`${task.id}-${role.roleSlotGuid}`}
                    className={`${styles.raciCell} ${isMine ? styles.myRoleCell : ""} ${isEditable ? styles.editableCell : ""} ${isActive ? styles.activeCell : ""}`}
                    onClick={isEditable ? (e) => handleCellClick(task.id, role.roleSlotGuid, e) : undefined}
                  >
                    {cell ? (
                      <span className={raciClassMap[cell.responsibilityType]}>
                        {raciMap[cell.responsibilityType]}
                      </span>
                    ) : (
                      isEditable && <span className={styles.emptyCellHint}>+</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Cell edit popover (portal) ── */}
      {popoverTarget &&
        canEdit &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.cellPopover}
            style={{
              position: "fixed",
              top: popoverTarget.rect.bottom + 4,
              left: popoverTarget.rect.left + popoverTarget.rect.width / 2,
              transform: "translateX(-50%)",
              zIndex: 100001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.popoverOptions}>
              {RESPONSIBILITY_TYPES.map((rType) => {
                const isCurrentType = popoverCell?.responsibilityType === rType;
                return (
                  <button
                    key={rType}
                    className={`${styles.popoverOption} ${isCurrentType ? styles.popoverOptionActive : ""}`}
                    onClick={() => handleSetType(rType)}
                    disabled={mutating}
                    title={t(`raci.${rType}`) || rType}
                  >
                    <span className={raciClassMap[rType]}>{raciMap[rType]}</span>
                    <span className={styles.popoverOptionLabel}>
                      {t(`raci.${rType}`) || rType}
                    </span>
                  </button>
                );
              })}
            </div>
            {popoverCell && (
              <button
                className={styles.popoverRemove}
                onClick={handleRemove}
                disabled={mutating}
              >
                {t("raci.remove") || "Remove"}
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default TeamRaciView;
